import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  BookOpen,
  Zap,
  Sparkles,
  Settings,
  Map,
  AlertCircle,
  FileText,
  ArrowRight,
  Download,
  Github,
} from 'lucide-react';
import { docsStructure } from '../docs';

const iconMap = {
  BookOpen,
  Zap,
  Sparkles,
  Settings,
  Map,
  AlertCircle,
  FileText,
};

const DocsHome = () => {
  return (
    <div className="space-y-12">
      {/* Hero Section */}
      <div className="text-center space-y-6 py-8">
        <h1 className="text-5xl md:text-6xl font-bold">
          <span className="bg-gradient-to-r from-purple-400 to-yellow-400 bg-clip-text text-transparent">
            Cogsmith Documentation
          </span>
        </h1>
        <p className="text-xl text-foreground/70 max-w-2xl mx-auto">
          Everything you need to master Cogsmith - from getting started to advanced automation workflows
        </p>
        
        <div className="flex flex-wrap items-center justify-center gap-4 pt-4">
          <Link to="/docs/getting-started/quick-start">
            <Button size="lg" className="bg-gradient-to-r from-purple-500 to-yellow-500 hover:from-purple-600 hover:to-yellow-600">
              Quick Start Guide
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
          <Link to="/download">
            <Button size="lg" variant="outline">
              <Download className="mr-2 h-5 w-5" />
              Download Cogsmith
            </Button>
          </Link>
          <a 
            href="https://github.com/newtro/Cogsmith" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <Button size="lg" variant="outline">
              <Github className="mr-2 h-5 w-5" />
              View on GitHub
            </Button>
          </a>
        </div>
      </div>

      {/* Documentation Sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {docsStructure.map(section => {
          const Icon = iconMap[section.icon] || FileText;
          
          return (
            <Card 
              key={section.id}
              className="group hover:shadow-xl hover:shadow-purple-500/10 transition-all duration-300 hover:-translate-y-1 bg-card/50 backdrop-blur-sm border-border/40 hover:border-purple-500/30"
            >
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-2xl">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500/20 to-yellow-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Icon className="h-6 w-6 text-purple-400" />
                  </div>
                  {section.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {section.pages.slice(0, 4).map(page => (
                  <Link
                    key={page.slug}
                    to={`/docs/${section.id}/${page.slug}`}
                    className="block group/link"
                  >
                    <div className="flex items-start justify-between gap-2 p-2 rounded-lg hover:bg-purple-500/10 transition-colors">
                      <div className="flex-1">
                        <div className="font-medium text-foreground group-hover/link:text-purple-400 transition-colors">
                          {page.title}
                        </div>
                        {page.description && (
                          <div className="text-sm text-foreground/60 mt-0.5">
                            {page.description}
                          </div>
                        )}
                      </div>
                      <ArrowRight className="h-4 w-4 text-purple-400 flex-shrink-0 mt-1 opacity-0 group-hover/link:opacity-100 group-hover/link:translate-x-1 transition-all" />
                    </div>
                  </Link>
                ))}
                
                {section.pages.length > 4 && (
                  <Link
                    to={`/docs/${section.id}/${section.pages[0].slug}`}
                    className="block text-sm text-purple-400 hover:text-purple-300 font-medium mt-2"
                  >
                    View all {section.pages.length} articles →
                  </Link>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Links */}
      <div className="bg-gradient-to-br from-purple-500/10 to-yellow-500/10 rounded-lg p-8 border border-purple-500/20">
        <h2 className="text-2xl font-bold mb-6">Popular Topics</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link 
            to="/docs/core-features/browser-automation"
            className="p-4 rounded-lg bg-card/50 hover:bg-card transition-colors border border-border/40 hover:border-purple-500/30"
          >
            <h3 className="font-semibold mb-1">Browser Automation</h3>
            <p className="text-sm text-foreground/60">Control browsers with AI</p>
          </Link>
          
          <Link 
            to="/docs/advanced-features/hooks-system"
            className="p-4 rounded-lg bg-card/50 hover:bg-card transition-colors border border-border/40 hover:border-purple-500/30"
          >
            <h3 className="font-semibold mb-1">Hooks System</h3>
            <p className="text-sm text-foreground/60">Customize AI behavior</p>
          </Link>
          
          <Link 
            to="/docs/advanced-features/automated-testing"
            className="p-4 rounded-lg bg-card/50 hover:bg-card transition-colors border border-border/40 hover:border-purple-500/30"
          >
            <h3 className="font-semibold mb-1">Automated Testing</h3>
            <p className="text-sm text-foreground/60">Create and run tests</p>
          </Link>
        </div>
      </div>

      {/* Help Section */}
      <div className="text-center space-y-4 py-8">
        <h2 className="text-2xl font-bold">Need Help?</h2>
        <p className="text-foreground/70">
          Can't find what you're looking for? Check out these resources:
        </p>
        <div className="flex flex-wrap items-center justify-center gap-4">
          <a 
            href="https://github.com/newtro/Cogsmith/issues" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <Button variant="outline">
              Report an Issue
            </Button>
          </a>
          <a 
            href="https://github.com/newtro/Cogsmith/discussions" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <Button variant="outline">
              Join Discussions
            </Button>
          </a>
          <Link to="/contact">
            <Button variant="outline">
              Contact Support
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DocsHome;

